/**
 * @file MultiSelect 多选下拉
 * @description 多选下拉；可选搜索；触发器展示已选 Tag；Portal + 视口边界 + fade。
 *
 * @whenToUse 标签、权限、多分类筛选。
 * @whenNotToUse 单选（用 Select / Combobox）；大量勾选列表（用 Checkbox 组）。
 */

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import {
  computeDropdownBox,
  floatingPanelStyle,
  type FloatingBox,
} from '@/lib/floating.ts'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'

/** 搜索栏占位高度（含边框区），用于扣减面板列表高度 */
const SEARCH_BAR_HEIGHT = 56

export type MultiSelectOption = {
  /** 选项值 */
  value: string
  /** 展示文案 */
  label: string
  disabled?: boolean
}

export type MultiSelectProps = {
  options: MultiSelectOption[]
  /** 已选值列表 */
  value?: string[]
  onChange?: (value: string[]) => void
  placeholder?: string
  /** 是否展示搜索框；默认不展示 */
  showSearch?: boolean
  searchPlaceholder?: string
  disabled?: boolean
  invalid?: boolean
  className?: string
  id?: string
  emptyText?: ReactNode
  /** 触发器最多展示几个 Tag，超出显示 +N */
  maxTagCount?: number
}

export function MultiSelect({
  options,
  value = [],
  onChange,
  placeholder = '请选择',
  showSearch = false,
  searchPlaceholder = '搜索…',
  disabled = false,
  invalid = false,
  className,
  id,
  emptyText = '无匹配结果',
  maxTagCount = 2,
}: MultiSelectProps) {
  const listId = useId()
  const triggerRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [box, setBox] = useState<FloatingBox | null>(null)
  const [query, setQuery] = useState('')

  const selectedOptions = options.filter((item) => value.includes(item.value))
  const filtered = useMemo(() => {
    if (!showSearch) return options
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((item) => item.label.toLowerCase().includes(q))
  }, [options, query, showSearch])

  const updateBox = () => {
    const el = triggerRef.current
    if (!el) return
    setBox(computeDropdownBox(el.getBoundingClientRect()))
  }

  useEffect(() => {
    if (open) {
      setMounted(true)
      setQuery('')
      updateBox()
      const frame = requestAnimationFrame(() => {
        setVisible(true)
        if (showSearch) inputRef.current?.focus()
      })
      return () => cancelAnimationFrame(frame)
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), 200)
    return () => window.clearTimeout(timer)
  }, [open, showSearch])

  useEffect(() => {
    if (!open) return
    const onWin = () => updateBox()
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    return () => {
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onPointer = (event: MouseEvent) => {
      const t = event.target as Node
      if (triggerRef.current?.contains(t)) return
      if (panelRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const toggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange?.(value.filter((item) => item !== optionValue))
      return
    }
    onChange?.([...value, optionValue])
  }

  const visibleTags = selectedOptions.slice(0, maxTagCount)
  const hiddenCount = selectedOptions.length - visibleTags.length
  const listOffset = showSearch ? SEARCH_BAR_HEIGHT : 0

  return (
    <>
      <div
        id={id}
        ref={triggerRef}
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-disabled={disabled || undefined}
        className={cn(
          'inline-flex min-h-9 w-full cursor-pointer items-center justify-between gap-2 rounded-lg border-2 bg-raised px-3 py-1.5 text-left text-sm transition-opacity duration-200',
          invalid ? 'border-danger' : 'border-border',
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
        onClick={() => {
          if (disabled) return
          setOpen((v) => !v)
        }}
        onKeyDown={(event) => {
          if (disabled) return
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setOpen((v) => !v)
          }
        }}
      >
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {selectedOptions.length === 0 ? (
            <span className="text-muted">{placeholder}</span>
          ) : (
            <>
              {visibleTags.map((item) => (
                <span
                  key={item.value}
                  className="inline-flex max-w-28 items-center gap-1 truncate rounded-sm bg-accent/25 px-1.5 py-0.5 text-xs text-foreground"
                >
                  <span className="truncate">{item.label}</span>
                  <button
                    type="button"
                    aria-label={`移除 ${item.label}`}
                    className="shrink-0 text-muted transition-opacity duration-150 hover:opacity-90"
                    disabled={disabled}
                    onClick={(event) => {
                      event.stopPropagation()
                      toggle(item.value)
                    }}
                  >
                    <X className="size-3" aria-hidden />
                  </button>
                </span>
              ))}
              {hiddenCount > 0 ? (
                <span className="text-xs text-muted">+{hiddenCount}</span>
              ) : null}
            </>
          )}
        </span>
        <ChevronsUpDown className="size-4 shrink-0 text-muted" aria-hidden />
      </div>

      {mounted && box && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={panelRef}
              className={cn(
                'fixed z-50 overflow-hidden rounded-lg border border-border bg-raised transition-opacity duration-200',
                visible ? 'opacity-100' : 'opacity-0',
              )}
              style={floatingPanelStyle(box)}
            >
              {showSearch ? (
                <div className="border-b border-border p-2">
                  <input
                    ref={inputRef}
                    value={query}
                    placeholder={searchPlaceholder}
                    className="h-9 w-full rounded-md border-2 border-border bg-raised px-3 text-sm text-foreground"
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </div>
              ) : null}
              <ScrollArea
                className="w-full"
                style={{
                  height: Math.min(
                    box.maxHeight - listOffset,
                    Math.max(filtered.length, 1) * 38 + 8,
                  ),
                }}
              >
                <div id={listId} role="listbox" aria-multiselectable className="p-1">
                  {filtered.length === 0 ? (
                    <p className="px-2 py-3 text-center text-sm text-muted">
                      {emptyText}
                    </p>
                  ) : (
                    filtered.map((option) => {
                      const isActive = value.includes(option.value)
                      return (
                        <button
                          key={option.value}
                          type="button"
                          role="option"
                          aria-selected={isActive}
                          disabled={option.disabled}
                          className={cn(
                            'flex h-9 w-full items-center justify-between gap-2 rounded-md px-2 text-left text-sm transition-opacity duration-200 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50',
                            isActive ? 'text-primary' : 'text-foreground',
                          )}
                          onClick={() => {
                            if (option.disabled) return
                            toggle(option.value)
                          }}
                        >
                          <span className="min-w-0 truncate">{option.label}</span>
                          {isActive ? (
                            <Check className="size-3.5 shrink-0" aria-hidden />
                          ) : null}
                        </button>
                      )
                    })
                  )}
                </div>
              </ScrollArea>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
